import { UserProfile } from '@/types/user';
import { Obra } from '@/types/obra';

/**
 * Determines if a user should be redirected to a single obra's info page
 * and returns the redirect URL if applicable.
 * 
 * Conditions for redirect:
 * - User is not an admin
 * - User has exactly one active obra
 * 
 * @param userProfile - The authenticated user's profile
 * @param obras - Array of obras assigned to the user
 * @returns Redirect URL if conditions are met, otherwise null
 */
export function getObraRedirectUrl(
  userProfile: UserProfile | null, 
  obras: Obra[]
): string | null {
  // No redirect if user profile is not available
  if (!userProfile) {
    return null;
  }

  // No redirect for admin users - they should see the full dashboard
  if (userProfile.role === 'admin') {
    return null;
  }

  // Filter for active obras only
  const activeObras = obras.filter(obra => obra.status === 'active');

  // Redirect only if user has exactly one active obra
  if (activeObras.length === 1) {
    return `/obras/${activeObras[0].id}/info`;
  }

  // No redirect for users with 0 or multiple active obras
  return null;
}

/**
 * Hook-like function to check if redirect should happen and perform it
 * This is used in components that need to check for redirect on mount
 */
export function shouldRedirectToSingleObra(
  userProfile: UserProfile | null,
  obras: Obra[]
): { shouldRedirect: boolean; redirectUrl: string | null } {
  const redirectUrl = getObraRedirectUrl(userProfile, obras);
  
  return {
    shouldRedirect: redirectUrl !== null,
    redirectUrl
  };
}